import * as React from 'react';

import {
  DEFAULT_TENANT_SLUG,
  adminTenantsForUser,
  getMembershipRole,
  resolveActiveTenant,
  type Membership,
  type Tenant,
  type TenantRole,
} from '@/lib/tenant';
import { supabase } from '@sixam/supabase/client';

import { useSession } from './session-provider';

type TenantContextValue = {
  tenants: Tenant[];
  activeTenant: Tenant | null;
  memberships: Membership[];
  membershipRole: TenantRole | null;
  isAnyTenantAdmin: boolean;
  isTenantAdmin: boolean;
  isTenantOwner: boolean;
  isTenantLoading: boolean;
  /** Point the context at the tenant identified by a URL `[tenant]` slug. */
  setActiveTenantSlug: (slug: string | null) => void;
  /** Slug to land on when no tenant is in the URL (e.g. index redirects). */
  resolveDefaultSlug: () => string | null;
};

const TenantContext = React.createContext<TenantContextValue | undefined>(undefined);

export function TenantProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const { isAdmin, session } = useSession();
  const userId = session?.user.id;

  const [tenants, setTenants] = React.useState<Tenant[]>([]);
  const [memberships, setMemberships] = React.useState<Membership[]>([]);
  const [activeTenantSlug, setActiveTenantSlug] = React.useState<string | null>(null);
  const [isTenantLoading, setIsTenantLoading] = React.useState(false);

  React.useEffect(() => {
    let active = true;

    async function loadTenantContext() {
      if (!userId) {
        if (active) {
          setTenants([]);
          setMemberships([]);
          setIsTenantLoading(false);
        }
        return;
      }

      setIsTenantLoading(true);

      const [tenantsResult, membershipsResult] = await Promise.all([
        supabase.from('tenants').select('*').order('slug'),
        supabase.from('memberships').select('*').eq('user_id', userId),
      ]);

      if (!active) {
        return;
      }

      setTenants(tenantsResult.data ?? []);
      setMemberships(membershipsResult.data ?? []);
      setIsTenantLoading(false);
    }

    void loadTenantContext();

    return () => {
      active = false;
    };
  }, [userId]);

  const activeTenant = React.useMemo(
    () => tenants.find((tenant) => tenant.slug === activeTenantSlug) ?? null,
    [tenants, activeTenantSlug]
  );

  const membershipRole = React.useMemo(
    () => getMembershipRole(memberships, activeTenant?.id, userId),
    [memberships, activeTenant?.id, userId]
  );

  const resolveDefaultSlug = React.useCallback(
    () => resolveActiveTenant(tenants, memberships, DEFAULT_TENANT_SLUG)?.slug ?? null,
    [tenants, memberships]
  );

  const value = React.useMemo<TenantContextValue>(
    () => ({
      tenants,
      activeTenant,
      memberships,
      membershipRole,
      isAnyTenantAdmin: adminTenantsForUser(tenants, memberships, isAdmin).length > 0,
      isTenantAdmin: membershipRole === 'admin' || membershipRole === 'owner',
      isTenantOwner: membershipRole === 'owner',
      isTenantLoading,
      setActiveTenantSlug,
      resolveDefaultSlug,
    }),
    [
      tenants,
      activeTenant,
      memberships,
      membershipRole,
      isTenantLoading,
      resolveDefaultSlug,
      isAdmin,
    ]
  );

  return <TenantContext value={value}>{children}</TenantContext>;
}

export function useTenant() {
  const context = React.useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
